import { useRef, useState } from "react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { useBrainRefresh } from "@/hooks/brain/useBrainSources";

/**
 * Run one write. The action toasts its own success. A throw → error toast, and
 * the card stays usable; a `false` return means nothing changed, same. Any
 * other outcome is done: the deck refreshes and advances, and the controls stay
 * disabled until the refetch drops the card, so a finished action (an assignee
 * email, a ticket-closed email) can't be sent a second time meanwhile.
 */
export function useBrainRun(onDone: () => void) {
  const { t } = useLanguage();
  const refresh = useBrainRefresh();
  const [busy, setBusy] = useState(false);
  // `busy` lags a render behind a fast double-click; this flips synchronously.
  const inFlight = useRef(false);
  const run = async (action: () => Promise<boolean | void>) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    let changed: boolean | void = false;
    try {
      changed = await action();
    } catch (e) {
      // Supabase errors are plain objects with a message, not Error instances.
      toast.error(
        (e as { message?: string } | null)?.message ||
          t("brain.actions.failed", "That didn't work — try again")
      );
      changed = false;
    }
    if (changed === false) {
      inFlight.current = false;
      setBusy(false);
      return;
    }
    void refresh();
    onDone();
  };
  return { run, busy };
}
